import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { TraceComment, TraceItem } from "../types";
import { getTrace } from "../api";

/** URL query param that deep-links a single trace's detail on the Traces page. */
export const TRACE_PARAM = "trace";

/**
 * Drives the trace-detail panel off the `?trace=<id>` URL param so the Traces
 * page is deep-linkable — e.g. from the failure-clusters panel (#1278).
 *
 * The selected trace is read from the currently loaded page when present;
 * otherwise it is fetched by id, since the list is paginated and a linked trace
 * is frequently not on the page the user has loaded. Optimistic rating/comment
 * edits are mirrored onto the fetched trace (on-page rows are updated by
 * `useTraces`).
 */
export function useSelectedTrace(loadedTraces: TraceItem[]) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get(TRACE_PARAM);

  const [fetchedTrace, setFetchedTrace] = useState<TraceItem | null>(null);
  const [fetching, setFetching] = useState(false);
  // True when an off-page fetch fails (stale / invalid / cross-domain id, or a
  // network error). Lets the open panel show a "not found" message instead of
  // the misleading "select a trace" empty state for a followed deep link.
  const [notFound, setNotFound] = useState(false);

  const onPage = useMemo(
    () => loadedTraces.find((t) => t.id === selectedId) ?? null,
    [loadedTraces, selectedId],
  );

  // Fetch by id only when a trace is selected but not on the loaded page.
  useEffect(() => {
    if (!selectedId || onPage) {
      setFetchedTrace(null);
      setFetching(false);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    setNotFound(false);
    getTrace(selectedId)
      .then((t) => {
        if (!cancelled) setFetchedTrace(t);
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedTrace(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, onPage]);

  const selectTrace = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set(TRACE_PARAM, id);
      else next.delete(TRACE_PARAM);
      // replace: selecting/closing traces shouldn't pile up history entries.
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const applyRating = useCallback((id: string, rating: boolean) => {
    setFetchedTrace((prev) =>
      prev && prev.id === id ? { ...prev, rating } : prev,
    );
  }, []);

  const applyComment = useCallback((id: string, comment: TraceComment) => {
    setFetchedTrace((prev) =>
      prev && prev.id === id
        ? { ...prev, comments: [...prev.comments, comment] }
        : prev,
    );
  }, []);

  return {
    selectedId,
    selectedTrace: onPage ?? fetchedTrace,
    loading: fetching,
    notFound,
    selectTrace,
    applyRating,
    applyComment,
  };
}
